import React, { useState } from 'react';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CampaignPackageView from '../../../../shared/packages/components/CampaignPackageView';
import { CampaignManagementUser } from './CampaignManagementUser';
import { CampaignManagementModal } from './modal/CampaignManagementModal';
import { useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { IconManagement } from '../../../../../shared/Icons';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import CampaignClientManagementModal from './modal/CampaignClientManagementModal';
import { ICampaignManagement } from './dto/ICampaignManagement';
import { hasAccessRights } from '../../../../../shared/functions/Functions';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import { UserRoles } from '../../../../../enum/UserRoles';
import Tooltip from '@mui/material/Tooltip';
import { MUTED_TEXT_COLOR } from '../../../../../utils/constants';

interface ICampaignManagementProps {
    setUpdateManagement: React.Dispatch<React.SetStateAction<boolean>>;
    managementInfo?: ICampaignManagement;
}

export function CampaignManagement({ managementInfo, setUpdateManagement }: ICampaignManagementProps) {
    const [show, setShow] = useState(false);
    const [showClientModal, setShowClientModal] = useState(false);
    const [campaignId, setCampaignId] = useState(0);

    const userType = useSelector((state: IAllStates) => state?.auth?.user?.userType);
    const isTeamCollaborationEnabled = useSelector(
        (state: IAllStates) => state?.auth?.user?.company?.package?.teamCollaboration
    );
    const sortedUsers = managementInfo?.isSupplier ? managementInfo?.assignedUsers : managementInfo?.clientUsers;
    const title = useSelector((state: IAllStates) => state.selectedCampaign?.campaign?.title);

    const usersManagingCampaign = sortedUsers?.sort((a, b) => {
        if (a.userType === 'brand_master' && b.userType !== 'brand_master') {
            return -1;
        } else if (a.userType !== 'brand_master' && b.userType === 'brand_master') {
            return 1;
        } else {
            return 0;
        }
    });

    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();

    const showModalAndSetId = (id: string) => {
        setCampaignId(+id);
        setShow(true);
    };

    const closeModal = () => {
        setShow(false);
        setUpdateManagement(prevState => !prevState);
    };
    const closeClientModal = () => {
        setShowClientModal(false);
        setUpdateManagement(prevState => !prevState);
    };

    return (
        <>
            <CampaignManagementModal
                show={show}
                closeModal={closeModal}
                openModal={() => setShow(true)}
                campaignId={campaignId}
                assignedUsers={usersManagingCampaign ?? []}
                campaignName={title ?? ''}
            />
            <CampaignClientManagementModal
                isSupplier={managementInfo?.isSupplier}
                show={showClientModal}
                onClose={closeClientModal}
            />

            <div className="d-flex justify-content-between align-items-center">
                <h6
                    className="textHover cursor-pointer"
                    onClick={() =>
                        isTeamCollaborationEnabled && userType === UserRoles.BRAND_MASTER && showModalAndSetId(id!)
                    }
                >
                    <IconManagement width="20" height="20" color={MUTED_TEXT_COLOR} classes={' mr-2'} />
                    {t('brand.campaign.dashboard.campaign_management.campaign_management')}
                </h6>
            </div>
            {isTeamCollaborationEnabled ? (
                <>
                    {(usersManagingCampaign?.length ?? 0) > 0 && (
                        <Row className="align-items-center mt-2">
                            {usersManagingCampaign?.slice(0, 2)?.map(userData => {
                                return (
                                    <CampaignManagementUser
                                        user={userData}
                                        classParent="pr-0"
                                        brandCompany={managementInfo?.ownedBy?.brandCompany}
                                        key={userData?.id}
                                        hasMultipleUsers={usersManagingCampaign?.length > 1}
                                    />
                                );
                            })}

                            {(usersManagingCampaign?.length ?? 0) > 2 && (
                                <div className="col-auto pl-1">
                                    <Tooltip
                                        id="white-tooltip"
                                        title={
                                            <>
                                                {usersManagingCampaign?.slice(2)?.map(campaign => (
                                                    <div className="text-dark p-1" key={campaign?.id}>
                                                        <CampaignManagementUser
                                                            user={campaign}
                                                            brandCompany={managementInfo?.ownedBy?.brandCompany}
                                                            key={campaign?.id}
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        }
                                        placement="left"
                                        className="cursor-pointer"
                                    >
                                        <span
                                            className="icon-round bg-white shadow text-primary font-weight-semi-bold"
                                            style={{ width: 36, maxWidth: 36 }}
                                        >
                                            + {(usersManagingCampaign?.length ?? 0) - 2}
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                        </Row>
                    )}
                </>
            ) : (
                <CampaignPackageView />
            )}

            {isTeamCollaborationEnabled && (
                <div className="d-flex justify-content-between mt-3">
                    <p className="text-muted mb-0">
                        {t('brand.campaign.dashboard.campaign_management.managed_by')} :{' '}
                        {managementInfo?.ownedBy?.brandCompany?.displayName}
                    </p>
                    {isTeamCollaborationEnabled && (
                        <div>
                            {userType !== UserRoles.BRAND && (
                                <a
                                    className="cursor-pointer text-uppercase"
                                    onClick={() => {
                                        showModalAndSetId(id!);
                                    }}
                                >
                                    {t('brand.campaign.dashboard.campaign_management.manage')}
                                </a>
                            )}

                            {hasAccessRights(AccessRightEnum.CAN_SHARE_CAMPAIGN) &&
                                managementInfo?.canShare &&
                                !managementInfo?.clientUsers?.length && (
                                    <a
                                        className="cursor-pointer text-uppercase ml-3"
                                        onClick={() => setShowClientModal(true)}
                                    >
                                        {t('brand.campaign.dashboard.campaign_management.share')}
                                    </a>
                                )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
